import { ErrorReporter } from '@wix/editor-error-reporter';
import { HttpClient, RequestOptions } from '@wix/http-client';
import { ADD_PRESET_SERVERLESS_BASE_URL } from '../siteGeneratorUtilsConsts';
import {
  deletePropsFromStructure,
  getLegalPagesByAppDefId,
  removeDeadComponents,
} from './siteGeneratorUtils';
import { getBusinessTermByName } from '@wix/ambassador-funnel-taxonomy-v2-business-term/http';
import { suggest } from '@wix/ambassador-funnel-taxonomy-v2-business-term-recommendation/http';
import type {
  SerializedCompStructure,
  SerializedPageStructure,
} from '@wix/document-services-types';
import { PresetContentCategory } from '@wix/editor-content-provider';
import {
  TEXT_ON_IMAGE_FLOWS,
  TextOnImageFlowsDefinition,
} from '../aiImageGenerator/types';
import { reportErrorFromGPT } from '../chatController/biUtilts';
import { ChatTopic } from '../chatController/constants';
import { LEGAL_PAGES_BY_LANG } from '../legalPagesConsts';
import {
  FooterPresetDefinition,
  HeaderPresetDefinition,
  LayoutFamilyDefinition,
  PagePresetDefinition,
  SectionPresetDefinition,
  SendBi,
} from '../types';

const httpClient = new HttpClient();

const requestData = (url: string, requestOptions: RequestOptions = {}) => {
  return httpClient
    .get(url, requestOptions)
    .then(({ data }) => data)
    .catch((e): unknown[] => {
      ErrorReporter.captureException(e, {
        tags: { siteGeneratorRequest: true },
        extra: { url, requestOptions },
      });
      return [];
    });
};

export const getSectionsPresetsFromServerless = (
  contentCategory: PresetContentCategory,
  layoutFamily: string,
): Promise<SectionPresetDefinition[]> => {
  return requestData(
    `${ADD_PRESET_SERVERLESS_BASE_URL}sections/?contentCategory=${contentCategory}&layoutFamily=${layoutFamily}`,
  );
};

export const getSectionsPresetsFromProduction = (
  contentCategory: string,
  layoutFamily: string,
  presetsBundleBaseUrl: string,
): Promise<SectionPresetDefinition[]> => {
  return requestData(
    `${presetsBundleBaseUrl}/sections_${layoutFamily}_${contentCategory}.json`,
  );
};

export const getSectionsPresets = (
  contentCategory: PresetContentCategory,
  layoutFamily: string,
  isStagingMode: boolean,
  presetsBundleBaseUrl: string,
): Promise<SectionPresetDefinition[]> => {
  return isStagingMode
    ? getSectionsPresetsFromServerless(contentCategory, layoutFamily)
    : getSectionsPresetsFromProduction(
        contentCategory,
        layoutFamily,
        presetsBundleBaseUrl,
      );
};

export const getFootersFromServerless = (
  layoutFamily: string,
): Promise<FooterPresetDefinition[]> => {
  return requestData(
    `${ADD_PRESET_SERVERLESS_BASE_URL}footers/?layoutFamily=${layoutFamily}`,
  );
};

export const getFootersFromProduction = (
  layoutFamily: string,
  presetsBundleBaseUrl: string,
): Promise<FooterPresetDefinition[]> => {
  return requestData(`${presetsBundleBaseUrl}/footers_${layoutFamily}.json`);
};

export const getFooters = async (
  layoutFamily: string,
  isStagingMode: boolean,
  presetsBundleBaseUrl: string,
): Promise<FooterPresetDefinition[]> => {
  return isStagingMode
    ? getFootersFromServerless(layoutFamily)
    : getFootersFromProduction(layoutFamily, presetsBundleBaseUrl);
};

const getHeadersFromServerless = (
  layoutFamily: string,
): Promise<HeaderPresetDefinition[]> => {
  return requestData(
    `${ADD_PRESET_SERVERLESS_BASE_URL}headers/?layoutFamily=${layoutFamily}`,
  );
};

const getHeadersFromProduction = (
  layoutFamily: string,
  presetsBundleBaseUrl: string,
): Promise<HeaderPresetDefinition[]> => {
  return requestData(`${presetsBundleBaseUrl}/headers_${layoutFamily}.json`);
};

export const getHeaders = async (
  layoutFamily: string,
  isStagingMode: boolean,
  presetsBundleBaseUrl: string,
): Promise<HeaderPresetDefinition[]> => {
  return isStagingMode
    ? getHeadersFromServerless(layoutFamily)
    : getHeadersFromProduction(layoutFamily, presetsBundleBaseUrl);
};

const fetchPresetStructure = (
  presetJsonUrl: string,
): Promise<SerializedCompStructure> => {
  return requestData(presetJsonUrl);
};

export const fetchPresetStructures = async (
  presetJsonUrls: string[],
): Promise<SerializedCompStructure[]> => {
  const structures = await Promise.all(
    presetJsonUrls.map(fetchPresetStructure),
  );
  structures.forEach(deletePropsFromStructure);
  structures.forEach(removeDeadComponents);
  return structures;
};

const fetchLayoutFamiliesFromServerless = (): Promise<
  LayoutFamilyDefinition[]
> => {
  return requestData(`${ADD_PRESET_SERVERLESS_BASE_URL}layoutFamilies`);
};

const fetchLayoutFamiliesFromProduction = (
  presetsBundleBaseUrl: string,
): Promise<LayoutFamilyDefinition[]> => {
  return requestData(`${presetsBundleBaseUrl}/layoutFamilies.json`);
};

export const fetchLayoutFamilies = (
  isStagingMode: boolean,
  presetsBundleBaseUrl: string,
): Promise<LayoutFamilyDefinition[]> => {
  return isStagingMode
    ? fetchLayoutFamiliesFromServerless()
    : fetchLayoutFamiliesFromProduction(presetsBundleBaseUrl);
};

export const fetchPageById = async (
  id: string,
  language: string,
): Promise<PagePresetDefinition | undefined> => {
  try {
    const response = await httpClient.get<PagePresetDefinition>(
      `${ADD_PRESET_SERVERLESS_BASE_URL}pages?language=${language}&id=${id}`,
    );

    return response.data;
  } catch (e) {
    ErrorReporter.captureException(e);
  }
};

const FALLBACK_LANG_CODE = 'en';

const isPagePresetDefinition = (
  response?: PagePresetDefinition | undefined,
): response is PagePresetDefinition => !!response;
const hasPageJsonUrl = (response?: PagePresetDefinition): boolean => {
  return !!response?.pageJsonUrl;
};

const fetchWithFallback = async (
  id: string,
  language: keyof typeof LEGAL_PAGES_BY_LANG,
  fallbackLanguage: keyof typeof LEGAL_PAGES_BY_LANG,
) => {
  let response = await fetchPageById(id, language);

  if (!hasPageJsonUrl(response)) {
    response = await fetchPageById(id, fallbackLanguage);
  }

  return response;
};

export const fetchPagesByIds = async (
  pageIds: string[],
  language: keyof typeof LEGAL_PAGES_BY_LANG,
  fallbackLangCode: keyof typeof LEGAL_PAGES_BY_LANG = FALLBACK_LANG_CODE,
): Promise<PagePresetDefinition[]> => {
  const pages = await Promise.all(
    pageIds.map((id) => fetchWithFallback(id, language, fallbackLangCode)),
  );

  return pages.filter(isPagePresetDefinition).filter(hasPageJsonUrl);
};

export const fetchPageStructure = async (pageJsonUrl?: string) => {
  if (!pageJsonUrl) return;

  try {
    const response = await httpClient.get<SerializedPageStructure>(pageJsonUrl);

    return response.data;
  } catch (e) {
    ErrorReporter.captureException(e);
  }
};

export const getLegalPages = async (
  chosenApps: string[],
  langCode: keyof typeof LEGAL_PAGES_BY_LANG,
) => {
  const ids = getLegalPagesByAppDefId(chosenApps, langCode);
  const legalPagesStructures = await fetchPagesByIds(ids, langCode);

  return await Promise.all(
    legalPagesStructures.map(async ({ pageJsonUrl, pageTitleKey }) => {
      return {
        pageTitleKey,
        structure: await fetchPageStructure(pageJsonUrl),
      };
    }),
  );
};

export const fetchTextOnImageBySectionAndFlow = (
  sectionName: string,
  flow: TEXT_ON_IMAGE_FLOWS,
): Promise<TextOnImageFlowsDefinition> => {
  return requestData(`${ADD_PRESET_SERVERLESS_BASE_URL}textOnImageFlows/`, {
    params: { sectionName, flow },
  });
};

export const fetchTaasBusinessType = async (
  businessType: string,
  sendBi?: SendBi,
): Promise<string> => {
  try {
    const response = await httpClient.request(suggest({ q: businessType }));
    const taasBusinessTypes = response.data.businessTermRecommendations?.find(
      (item) => item.displayName === businessType,
    )?.businessTerm?.businessType;

    return taasBusinessTypes ?? '';
  } catch (error: any) {
    const errorResponse = error.response?.data;

    if (sendBi)
      reportErrorFromGPT(
        errorResponse?.message || 'error response',
        errorResponse?.errorCode,
        'fetchTaasBusinessType',
        ChatTopic.Business,
        '{}',
        sendBi,
      );

    return '';
  }
};

export const getBusinessTermId = async (
  businessTypeName: string,
  locale: string,
) => {
  try {
    const response = await httpClient.request(
      getBusinessTermByName({ name: businessTypeName, locale }),
    );

    return response.data.businessTerm?.id;
  } catch (error: any) {
    const errorResponse = error.response?.data;
    ErrorReporter.captureException(error, {
      tags: { siteGeneratorRequest: true },
      extra: { errorResponse },
    });
  }
};
